import type { Dispatch, SetStateAction } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ConnectorEditorFields } from "@/components/composer/ConnectorEditorFields";
import { ConnectorTypeIcon } from "@/components/composer/ConnectorTypeIcon";
import {
  CONNECTOR_TYPE_LABELS, type EditorState,
} from "@/components/composer/connectorManagerHelpers";
import type { useWordPressGateway } from "@/components/composer/useWordPressGateway";

const CONNECTOR_TYPE_OPTIONS = [
  "webhook", "email", "kit", "bento", "beehiiv", "buttondown", "wordpress",
  "wordpress_self_hosted", "ghost", "substack_unofficial",
] as const;

const describeType = (type: EditorState["connectorType"]): string => {
  switch (type) {
    case "email":
      return "Sends prepared publications by email to a static recipient list (max 25).";
    case "kit":
      return "Creates a draft broadcast in your Kit (formerly ConvertKit) account when you dispatch a prepared publication.";
    case "bento":
      return "Creates a draft broadcast in your Bento (bentonow.com) account when you dispatch a prepared publication.";
    case "beehiiv":
      return "Creates a draft post in your Beehiiv publication using a V2 API key (Settings → Integrations → API).";
    case "buttondown":
      return "Creates a draft email in your Buttondown account using an API key (Settings → API). Buttondown is single-newsletter-per-account, so no extra ID is required.";
    case "wordpress":
      return "Creates a draft post on a WordPress.com site you've linked to this workspace. We handle authentication via the Lovable connector — no app password needed.";
    case "wordpress_self_hosted":
      return "Creates a draft post on your own self-hosted WordPress site using an Application Password generated in wp-admin (Users → Profile → Application Passwords).";
    case "ghost":
      return "Creates a draft post in your Ghost site (Ghost(Pro) or self-hosted) using a custom integration's Admin API key.";
    // bluesky/mastodon retired 2026-07-03.
    default:
      return "POSTs a JSON envelope to the target URL when you dispatch a prepared publication.";
  }
};

type WordPressGateway = ReturnType<typeof useWordPressGateway>;

type Props = {
  editor: EditorState;
  setEditor: Dispatch<SetStateAction<EditorState>>;
  savingEditor: boolean;
  closeEditor: () => void;
  onSave: () => void;
  wp: WordPressGateway;
  addRecipientFromDraft: () => void;
  removeRecipient: (email: string) => void;
};

export const ConnectorEditorDialog = ({
  editor,
  setEditor,
  savingEditor,
  closeEditor,
  onSave,
  wp,
  addRecipientFromDraft,
  removeRecipient,
}: Props) => {
  return (
    <Dialog open={editor.open} onOpenChange={(open) => (open ? null : closeEditor())}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editor.connector
              ? `Edit ${CONNECTOR_TYPE_LABELS[editor.connectorType]} connector`
              : `New ${CONNECTOR_TYPE_LABELS[editor.connectorType]} connector`}
          </DialogTitle>
          <DialogDescription>{describeType(editor.connectorType)}</DialogDescription>
        </DialogHeader>

        {!editor.connector ? (
          <div className="flex flex-wrap gap-2 rounded-md border border-border bg-muted/40 p-1 text-xs">
            {CONNECTOR_TYPE_OPTIONS.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setEditor((s) => ({ ...s, connectorType: t }))}
                className={`flex-1 min-w-[6rem] rounded px-2 py-1.5 transition-colors ${
                  editor.connectorType === t
                    ? "bg-background font-medium text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <span className="inline-flex items-center gap-1.5">
                  <ConnectorTypeIcon type={t} className="h-3.5 w-3.5" /> {CONNECTOR_TYPE_LABELS[t]}
                </span>
              </button>
            ))}
          </div>
        ) : null}

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="connector-name">Display name</Label>
            <Input
              id="connector-name"
              value={editor.displayName}
              onChange={(e) => setEditor((s) => ({ ...s, displayName: e.target.value }))}
              placeholder={editor.connectorType === "email" ? "e.g. Newsletter list" : "e.g. Newsletter relay"}
            />
          </div>

          <ConnectorEditorFields
            editor={editor}
            setEditor={setEditor}
            wpStatusLoading={wp.wpStatusLoading}
            wpLinked={wp.wpLinked}
            wpSites={wp.wpSites}
            wpSitesLoading={wp.wpSitesLoading}
            wpSitesError={wp.wpSitesError}
            loadWpSites={wp.loadWpSites}
            addRecipientFromDraft={addRecipientFromDraft}
            removeRecipient={removeRecipient}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={closeEditor} disabled={savingEditor}>
            Cancel
          </Button>
          <Button
            onClick={onSave}
            disabled={
              savingEditor ||
              (editor.connectorType === "wordpress" && (wp.wpStatusLoading || wp.wpLinked === false))
            }
          >
            {savingEditor ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {editor.connector ? "Save changes" : "Create connector"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
